import { TextLayout } from '@st/document-rendering'
import { Rect, Size } from '@st/geom'
import { Font, StampAnnotation } from '@st/sdk'
import { STAMP_GLYPHS } from '@st/document-components/annotations/stamp-glyphs'
import { match, P } from '@st/pattern'

export type StampLayout = {
  size: Size
  fontSize: number
  textBounds: Rect
}

/**
 * The layout of a stamp is all calculated based on the stampSize which corresponds to the
 * of the exact height of the stamp.
 *
 * From here, the width and text placement are dynamically calculated based on the text,
 * fontSize, padding, etc.
 */
type LayoutOpts = {
  width?: number
}
export function recomputeStampLayoutAndStyle<T extends Omit<StampAnnotation, 'id' | 'time'>>(
  annotation: T,
  opts: LayoutOpts = {}
): T {
  const stampConfiguration = getStampStyleConfiguration(annotation.stampType)

  // The space remaining after the padding is the font size
  const stampHeight = annotation.stampSize
  const fontSize = Math.floor(stampHeight * stampConfiguration.fontScale)

  const font: Font = {
    family: 'Helvetica',
    size: fontSize,
    weight: 'normal'
  }

  const stampWidth = match(STAMP_GLYPHS[annotation.text])
    .with(P.nonNullable, () => stampHeight)
    .with(undefined, () => {
      const textDimensions = TextLayout.layout(annotation.text, font, 1.0)
      return Math.max(
        textDimensions.width + stampConfiguration.horizontalPadding * 2 * stampHeight,
        stampHeight
      )
    })
    .exhaustive()

  const newAnnotation = { ...annotation }

  newAnnotation.bounds = {
    x: annotation.bounds.x,
    y: annotation.bounds.y,
    width: stampWidth,
    height: stampHeight
  }

  newAnnotation.font = font

  // reserve the % border radius
  if (annotation.border && annotation.stampType == 'number') {
    newAnnotation.border = { ...annotation.border, radius: newAnnotation.bounds.height / 2 }
  } else if (annotation.stampType == 'tick_mark') {
    newAnnotation.border = {
      width: 0,
      radius: newAnnotation.bounds.height * 0.1
    }
  }

  if (opts?.width) {
    newAnnotation.bounds.width = opts.width
  }

  return newAnnotation as T
}

type StampStyleConfiguration = {
  fontScale: number
  verticalPadding: number
  horizontalPadding: number
}

function getStampStyleConfiguration(style: StampAnnotation['stampType']): StampStyleConfiguration {
  switch (style) {
    case 'cross_reference':
      return { fontScale: 0.6, verticalPadding: 0.2, horizontalPadding: 0.2 }
    case 'number':
      return { fontScale: 0.6, verticalPadding: 0.2, horizontalPadding: 0.2 }
    case 'sign_off':
      return { fontScale: 0.6, verticalPadding: 0.1, horizontalPadding: 0.2 }
    case 'tick_mark':
      // If there's no border or anything, we want the bounds to hug the text
      return { fontScale: 0.9, verticalPadding: 0, horizontalPadding: 0 }
  }
}
